import React, {
  ComponentProps,
  ComponentType,
  createContext,
  DOMAttributes,
  ForwardedRef,
  forwardRef,
  Key,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  Cell as _RACCell,
  CellProps as RACCellProps,
  Collection,
  CollectionRendererContext,
  Column as _RACColumn,
  ColumnProps as RACColumnProps,
  DefaultCollectionRenderer,
  Provider,
  Row as _RACRow,
  RowProps as RACRowProps,
  TableBody as _RACTableBody,
  TableBodyProps,
  TableProps as RACTableProps,
  TableHeader as _RACTableHeader,
  TableHeaderProps,
  TableStateContext,
  Virtualizer as _Virtualizer,
  CheckboxContext,
} from 'react-aria-components';
import {
  CollectionBuilder,
  createBranchComponent,
  createLeafComponent,
} from 'react-aria/CollectionBuilder';
import { useCachedChildren } from 'react-aria/private/collections/useCachedChildren';
import {
  type CellOverflow,
  type CellSpacing,
  ColumnAlignment,
  StickyPosition,
  Table as SapphireTable,
  TableContainerProps,
} from '../../Table';
import { Checkbox } from '../../Checkbox/src/Checkbox';
import styles from '@danske/sapphire-css/components/table/table.module.css';

import { DOMProps, forwardRefType, SelectionMode } from '@react-types/shared';
import { TableCollection as ITableCollection } from '@react-types/table';
import { sapphireRowClasses } from '../../Table/src/TableRow';
import { checkboxCellClasses } from '../../Table/src/TableSelectionCell';
import {
  headerCellClasses,
  HeaderCellContent,
} from '../../Table/src/TableHeaderCell';
import { getCellClassNames } from '../../Table/src/TableCell';
import {
  MultipleSelectionState,
  MultipleSelectionStateProps,
  useMultipleSelectionState,
} from 'react-stately/useMultipleSelectionState';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { mergeProps } from 'react-aria/mergeProps';
import { useId } from 'react-aria/useId';
import { useLoadMore } from 'react-aria/private/utils/useLoadMore';
import { useObjectRef } from 'react-aria/useObjectRef';
import { useTable, useTableCell, useTableRowGroup } from 'react-aria/useTable';
import { GridNode } from '@react-types/grid';
import { ToggleExpandButton } from '../../Table/src/RowExpandToggleCell';
import { tableClassNames } from '../../Table/src/Table';
import { RadioBox } from '../../Radio/src/RadioBox';
import { useFocusWithin } from 'react-aria/useFocusWithin';
import { FocusScope as _FocusScope } from 'react-aria/FocusScope';
import { useFocusRing } from 'react-aria/useFocusRing';
import { Skeleton } from '../../Skeleton';
import { PaginationContext } from '../../Pagination/src/usePaginationContext';
import { TableContainerContext } from '../../Table/src/TableContainerContext';
import clsx from 'clsx';
import {
  SapphireTableCollection,
  TableFooterNode,
} from './SapphireTableCollection';
import { useTableState } from './useTableState';
import {
  RowExpansionProps,
  useExpandableRowsState,
} from './useExpandableRowsState';
import { SapphireTableLayout } from './SapphireTableLayout';
import { WithResolveSelectAllToKeysKeys as WithSapphireSelectionManagerOptions } from '../../utils/patchSelectionManager';
import {
  GlobalDomAttributes,
  MultipleSelectionKeys,
  SortableKeys,
} from '../../utils/types';
import { SapphireStyleProps } from '../../utils';
import { TableLayoutProps } from 'react-stately/useVirtualizerState';

type FixTypeRegression<T> = T extends (p: infer P) => ReactNode
  ? (props: P) => ReactElement
  : T;

type AddProps<T, Props> = T extends React.ComponentType<infer P>
  ? ComponentType<P & Props>
  : T;

interface InternalRowProps {
  INTERNAL_nonActionable?: boolean;
  INTERNAL_nonSelectable?: boolean;
}
const RACRow = _RACRow as AddProps<
  FixTypeRegression<typeof _RACRow>,
  InternalRowProps
>;

interface ExtraColumnProps {
  isSticky?: boolean;
  renderSkeleton?: (index: number) => React.ReactNode;
}

const RACColumn = _RACColumn as AddProps<
  FixTypeRegression<typeof _RACColumn>,
  ExtraColumnProps
>;
const RACTableHeader = _RACTableHeader as FixTypeRegression<
  typeof _RACTableHeader
>;
const RACTableBody = _RACTableBody as FixTypeRegression<typeof _RACTableBody>;
interface ExtraCellProps {
  isSticky?: boolean;
}
const RACCell = _RACCell as AddProps<
  FixTypeRegression<typeof _RACCell>,
  ExtraCellProps
>;
const FocusScope = _FocusScope as FixTypeRegression<typeof _FocusScope>;
const Virtualizer = _Virtualizer as FixTypeRegression<typeof _Virtualizer>;

/**
 * Unrelated TODO:
 * - TooltipIfNeeded ref is null when switching from having overflow to not having overflow,
 * causing the tooltip to stay even when cellOverflow is changed from truncate to wrap.
 */

const ExpandedRowsStateContext = createContext<null | ReturnType<
  typeof useExpandableRowsState
>>(null);

const InteractiveTableOptionsContext = createContext<
  | (Pick<
      InteractiveTableProps,
      'highlightedRows' | 'loadingState' | 'tabIndex'
    > &
      Required<
        Pick<
          InteractiveTableProps,
          'cellOverflow' | 'loadingSkeletonRowsCount' | 'isNonVirtualized'
        >
      > & {
        isInteractive: boolean;
        isDisabled: boolean;
        selectionMode?: SelectionMode;
        disallowEmptySelection?: boolean;
      })
  | null
>(null);

const ColumnsContext = createContext<{
  columns: GridNode<object>[];
  updateColumns: () => void;
} | null>(null);

export type InteractiveTableProps = RowExpansionProps &
  WithSapphireSelectionManagerOptions<
    Pick<
      RACTableProps,
      MultipleSelectionKeys | SortableKeys | 'children' | 'selectionMode'
    >
  > &
  Pick<TableContainerProps, 'hideLastDivider' | 'footer'> &
  SapphireStyleProps &
  GlobalDomAttributes & {
    /**
     * A table must have an accessibility label.
     * This will not be visible to the average users.
     */
    'aria-label': string;

    /**
     * Allows custom position in the tabbing order (preserving focus on latest focused cell).
     * Use -1 to remove from the tab order.
     * Useful when you want a table to more closely represent a static table
     */
    tabIndex?: number;

    /**
     * Whether text within a table cell should be truncated and followed by
     * 3 dots or it should wrap.
     *
     * @default 'truncate'
     */
    cellOverflow?: CellOverflow;

    /**
     * The spacing inside table cells.
     * @default 'md'
     */
    cellSpacing?: CellSpacing;

    // TODO(v6): remove layout prop
    /**
     * Same as CSS table-layout property.
     * Only applicable for non-virtualized tables.
     * @default 'auto'
     * @deprecated layout "auto" is only applicable to native table layout which won't be available
     * in v6 where virtualization will be non-optional.
     */
    layout?: 'auto' | 'fixed';

    // TODO(v6): remove isNonVirtualized prop
    /**
     * Disables table virtualization and renders native table elements.
     * @default false
     * @deprecated InteractiveTable will be virtualized unconditionally in v6.
     * Read more about it in v5 release post.
     */
    isNonVirtualized?: boolean;

    /**
     * If the table header should be sticky relative to the first ancestor with
     * an `overflow`.
     *
     * @deprecated This option has no effect unless `isNonVirtualized` is set to true,
     * and will be removed in v6. Read more about it in V5 release post.
     * @default 'auto'
     */
    stickyHeader?: StickyPosition;

    /**
     * If the table's footer should be sticky relative to the first ancestor
     * with an `overflow`.
     * @deprecated This option has no effect unless `isNonVirtualized` is set to true,
     * and will be removed in v6. Read more about it in V5 release post.
     * @default 'auto'
     */
    stickyFooter?: StickyPosition;

    /**
     * Handler that is called when a user performs an action on a row.
     */
    onRowAction?: (key: Key) => void;

    // TODO(v6): remove renderSelectionActionBar prop
    /**
     * Optional function property designed to render a custom action bar based
     * on the selected rows in a table, identified by their keys.
     *
     * @deprecated Please render {@link BulkActionsBar} above the table instead.
     */
    renderSelectionActionBar?: (
      selectedRowKeys: React.Key[]
    ) => React.ReactNode;

    /**
     * keys of rows that should be highlighted. Highlighted rows are shown in a selected state
     * which is different from the normal selection with checkboxes. The primary use case is when
     * the row action is to update an external view (such as a row detail view), to
     * show its connection with the selected row.
     */
    highlightedRows?: Key[];

    /**
     * Disables selection and row action on the entire table
     * @default false
     */
    isDisabled?: boolean;

    /** Handler that is called when more items should be loaded, e.g. while scrolling near the bottom. */
    onLoadMore?: () => void;
    /** Whether table is loading or not. */
    loadingState?: 'loading' | 'loadingMore';
    /**
     * Number of loading skeleton rows to render.
     * Use it to match amount of actual content rows being loaded as closely as possible.
     * @default 3
     */
    loadingSkeletonRowsCount?: number;

    /**
     * Estimated height of each row in pixels.
     * Used for optimizing rendering performance.
     */
    estimatedRowHeight?: number;
  };

const DEFAULT_ROW_HEIGHTS = {
  sm: 40,
  md: 48,
  lg: 56,
  xl: 64,
};

export const InteractiveTable = (forwardRef as forwardRefType)(
  function InteractiveTable<T extends object>(
    {
      footer,
      stickyHeader = 'auto',
      stickyFooter = 'auto',
      cellOverflow = 'truncate',
      cellSpacing = 'md',
      expansionMode = 'none',
      expandedKeys: expandedKeysProp,
      onExpandedKeysChange,
      defaultExpandedKeys,
      onLoadMore,
      loadingState,
      loadingSkeletonRowsCount = 3,
      highlightedRows,
      layout = 'auto',
      isNonVirtualized = false,
      isDisabled = false,
      tabIndex,
      estimatedRowHeight,
      ...props
    }: InteractiveTableProps,
    forwardedRef: ForwardedRef<HTMLDivElement>
  ) {
    const containerRef = useObjectRef(forwardedRef);
    const ref = useRef<HTMLTableElement>(null);

    const expandedRowsState = useExpandableRowsState({
      expansionMode,
      expandedKeys: expandedKeysProp,
      onExpandedKeysChange,
      defaultExpandedKeys,
    });

    const onRowAction = props.onRowAction
      ? (key: Key) => {
          if (
            !(collectionRef.current?.getItem(key)?.props as InternalRowProps)
              .INTERNAL_nonActionable
          ) {
            props.onRowAction?.(key);
          }
        }
      : undefined;

    // Separate selection state so we have access to it from collection components via useTableOptions.
    const selectionState = useMultipleSelectionState({
      // The type cast is a hack to avoid the type error due to our resolveSelectAllToKeys option
      ...(props as Omit<MultipleSelectionStateProps, 'onSelectionChange'>),
    });

    const { selectionMode, disallowEmptySelection } = selectionState;
    const collectionRef = useRef<null | SapphireTableCollection<T>>(null);
    const [columns, setColumns] = useState<GridNode<object>[]>([]);
    const updateColumns = () => {
      const newColumns = collectionRef.current?.columns ?? [];
      if (
        columns.length !== newColumns.length ||
        columns.some(
          (column, index) =>
            column?.key !== collectionRef.current?.columns[index]?.key
        )
      ) {
        setColumns(newColumns);
      }
    };
    const isInteractive = onRowAction !== undefined || selectionMode !== 'none';

    const isLoading =
      loadingState === 'loading' || loadingState === 'loadingMore';
    useLoadMore(
      {
        isLoading,
        onLoadMore:
          onLoadMore &&
          (() => {
            requestAnimationFrame(onLoadMore);
          }),
      },
      ref
    );

    const tableOptionsContext = useMemo(
      () => ({
        isDisabled,
        isInteractive,
        selectionMode,
        disallowEmptySelection,
        cellOverflow,
        isNonVirtualized,
        highlightedRows,
        loadingState,
        loadingSkeletonRowsCount,
        tabIndex,
      }),
      [
        isDisabled,
        isInteractive,
        selectionMode,
        disallowEmptySelection,
        cellOverflow,
        isNonVirtualized,
        highlightedRows,
        loadingState,
        loadingSkeletonRowsCount,
        tabIndex,
      ]
    );
    const columnsContext = useMemo(
      () => ({ columns, updateColumns }),
      [columns]
    );
    const layoutOptions: TableLayoutProps = useMemo(
      () => ({
        // if estimatedHeadingHeight is not provided, it falls back to DEFAULT_ROW_HEIGHT which is 48, and it doesn't shrink to the content height which is 40
        estimatedHeadingHeight: 40,
        estimatedRowHeight:
          estimatedRowHeight ?? DEFAULT_ROW_HEIGHTS[cellSpacing],
      }),
      [cellSpacing]
    );
    const table = (
      <SapphireTable.Container
        ref={containerRef}
        {...props}
        stickyFooter={stickyFooter}
        stickyHeader={stickyHeader}
        isInteractive={isInteractive}
        footer={
          <PaginationContext.Provider
            value={{
              scrollableRef: isNonVirtualized ? containerRef : ref,
            }}
          >
            {footer}
          </PaginationContext.Provider>
        }
      >
        <Provider
          values={[
            [InteractiveTableOptionsContext, tableOptionsContext],
            [ColumnsContext, columnsContext],
          ]}
        >
          <CollectionBuilder
            content={
              <Provider
                // Note about performance: if the values here change on user interactions, it causes
                // the collection to be recreated and the virtualizer to do a relayout, which leads
                // to performance issues.
                values={[
                  [ExpandedRowsStateContext, expandedRowsState],
                  [InteractiveTableOptionsContext, tableOptionsContext],
                  [ColumnsContext, columnsContext],
                ]}
              >
                <Collection {...props} />
              </Provider>
            }
            createCollection={() => {
              collectionRef.current = new SapphireTableCollection<T>();
              return collectionRef.current;
            }}
          >
            {(collection) => {
              collectionRef.current = collection;
              return (
                // keeping the props interface of TableInner similar to RACTable, so we can replace
                // it at some point if they make it more flexible
                <>
                  <TableInner
                    className={tableClassNames({ cellSpacing, layout })}
                    selectionState={selectionState}
                    collection={collection}
                    forwardedRef={ref}
                    props={{
                      ...props,
                      isDisabled,
                      onRowAction,
                      tabIndex,
                      loadingState,
                    }}
                  />
                </>
              );
            }}
          </CollectionBuilder>
        </Provider>
      </SapphireTable.Container>
    );

    return isNonVirtualized ? (
      table
    ) : (
      <Virtualizer layout={SapphireTableLayout} layoutOptions={layoutOptions}>
        {table}
      </Virtualizer>
    );
  }
);

function TableInner({
  props,
  selectionState,
  className,
  collection,
  forwardedRef,
}: {
  // Omitting onSelectionChange is to avoid the type error due to our resolveSelectAllToKeys option
  props: Omit<InteractiveTableProps, 'layout' | 'onSelectionChange'>;
  className?: string;
  forwardedRef: ForwardedRef<HTMLTableElement>;
  selectionState: MultipleSelectionState;
  collection: ITableCollection<object>;
}) {
  const ref = useObjectRef(forwardedRef);
  const state = useTableState({
    ...props,
    children: undefined,
    collection: collection as SapphireTableCollection<object>,
    UNSAFE_selectionState: selectionState,
  });

  const { updateColumns } = useContext(ColumnsContext)!;
  useEffect(() => {
    updateColumns();
  });

  const { isVirtualized, layoutDelegate, CollectionRoot } = useContext(
    CollectionRendererContext
  );
  const { gridProps } = useTable(
    {
      ...props,
      layoutDelegate,
      isVirtualized,
    },
    state,
    ref
  );

  const { focusProps } = useFocusRing();

  const ElementType = isVirtualized ? 'div' : 'table';

  const persistedKeys = useMemo(
    () =>
      new Set(
        state.selectionManager.focusedKey
          ? [state.selectionManager.focusedKey]
          : []
      ),
    [state.selectionManager.focusedKey]
  );

  return (
    <TableStateContext.Provider value={state}>
      {state.selectionManager.selectionMode !== 'none' &&
        props.renderSelectionActionBar?.([
          ...state.selectionManager.selectedKeys,
        ])}
      <FocusScope>
        <ElementType
          {...filterDOMProps(props, { global: true })}
          {...mergeProps(gridProps, focusProps, {
            tabIndex: props.tabIndex ?? gridProps.tabIndex,
          })}
          aria-busy={props.loadingState && true}
          className={className}
          style={{ overflow: 'auto' }}
          ref={ref}
        >
          <CollectionRoot
            collection={collection}
            scrollRef={ref}
            persistedKeys={persistedKeys}
          />
        </ElementType>
      </FocusScope>
    </TableStateContext.Provider>
  );
}

export interface InteractiveTableColumnProps
  extends Pick<
    RACColumnProps,
    'id' | 'width' | 'maxWidth' | 'minWidth' | 'allowsSorting' | 'isRowHeader'
  > {
  children?: ReactNode;

  align?: ColumnAlignment;

  /**
   * A string representation of the column's header cell contents, used for accessibility
   * announcements.
   *
   * Also used for the tooltip text that's shown when overflow is set to "truncate" and the cell
   * content doesn't fit. If not provided, the text content of the cell is used for the tooltip.
   */
  textValue?: string;

  /**
   * Custom {@link InteractiveTableProps.cellOverflow} for this column.
   */
  overflow?: CellOverflow;
}

export const InteractiveTableColumn = forwardRef(
  function InteractiveTableColumn(
    props: InteractiveTableColumnProps,
    ref: ForwardedRef<HTMLElement>
  ) {
    const {
      children,
      align = 'left',
      width,
      minWidth,
      maxWidth,
      ...otherProps
    } = props;
    const textValue =
      !props.textValue && typeof props.children === 'string'
        ? props.children
        : props.textValue;

    // Table header height is not correctly measured based on the content,
    //  and is set to estimatedHeaderHeight (40), regardless of the content.
    //  if estimatedHeaderHeight is not provided, it will default to 48, but still
    //  not change based on the content height.
    // const { cellOverflow } = useContext(InteractiveTableOptionsContext)!;
    const overflow = 'truncate'; // FIXME: set `cellOverflow ?? props.overflow` when the issue above is fixed

    const { isNonVirtualized } = useContext(InteractiveTableOptionsContext)!;

    const sizeProps = isNonVirtualized
      ? {
          style: {
            width,
            maxWidth,
            minWidth,
          },
        }
      : { width, minWidth, maxWidth };
    return (
      <RACColumn
        {...otherProps}
        ref={ref as ForwardedRef<HTMLTableCellElement>}
        textValue={textValue}
        // @ts-expect-error align doesn't exist in RACColumn props
        align={align} // to be set on the column node so we can access it in Cell
        className={(renderProps) => {
          return headerCellClasses({
            ...renderProps,
            align,
          });
        }}
        // If we switch to rendering TableResizeContainer, we should pass width props to the RACColumn instead
        {...sizeProps}
      >
        {({ allowsSorting, sortDirection }) => (
          <HeaderCellContent
            textValue={textValue}
            allowsSorting={allowsSorting}
            overflow={overflow}
            sortDirection={sortDirection}
            align={align}
          >
            <Provider values={[[CheckboxContext, {}]]}>{children}</Provider>
          </HeaderCellContent>
        )}
      </RACColumn>
    );
  }
);

export const InteractiveTableHeader = (forwardRef as forwardRefType)(
  function TableHeader<T extends object>(
    { columns, children }: TableHeaderProps<T>,
    ref: ForwardedRef<HTMLElement>
  ) {
    const { hasToggleExpansionColumn } = useContext(ExpandedRowsStateContext)!;
    const { selectionMode, isDisabled, isNonVirtualized } = useContext(
      InteractiveTableOptionsContext
    )!;

    const { shouldHeaderStick } = useContext(TableContainerContext) ?? {};
    const cellStyles = isNonVirtualized ? {} : virtualizedCellStyles;
    return (
      <RACTableHeader
        // The ref type should have been more generic as it renders div or thead based on whether virtualization is applied
        ref={ref as ForwardedRef<HTMLTableSectionElement>}
        className={clsx(styles['sapphire-table__head'], {
          [styles['sapphire-table__head--sticky']]: shouldHeaderStick,
        })}
        style={isNonVirtualized ? {} : { height: '100%', width: '100%' }}
      >
        {hasToggleExpansionColumn && (
          <RACColumn
            isSticky
            renderSkeleton={() => null}
            minWidth={0}
            width={50}
            style={cellStyles}
          ></RACColumn>
        )}
        {selectionMode !== 'none' && (
          <RACColumn
            isSticky
            renderSkeleton={(index: number) => (
              <Skeleton.From
                index={index}
                margin="auto"
                data-testid="skeletonBar"
              >
                <Checkbox />
              </Skeleton.From>
            )}
            className={checkboxCellClasses}
            minWidth={0}
            // Providing exact width because content-based column sizing is not possible currently
            // in the virtualization implementation.
            width={50}
            style={cellStyles}
          >
            {selectionMode === 'multiple' && (
              <Checkbox isDisabled={isDisabled} INTERNAL_slot="selection" />
            )}
          </RACColumn>
        )}
        <Collection items={columns}>{children}</Collection>
      </RACTableHeader>
    );
  }
);

const FooterContext = createContext(false);

const TableFooter = createBranchComponent(
  TableFooterNode,
  (
    props: { children?: ReactNode },
    ref: ForwardedRef<HTMLTableSectionElement>
  ) => {
    const state = useContext(TableStateContext)!;
    const collection = state.collection as SapphireTableCollection<unknown>;
    const { isVirtualized, CollectionBranch } = useContext(
      CollectionRendererContext
    );
    const TFoot = isVirtualized ? 'div' : 'tfoot';
    const { rowGroupProps } = useTableRowGroup();
    return (
      <FooterContext.Provider value={true}>
        <TFoot
          {...mergeProps(
            filterDOMProps(props as DOMProps, { global: true }),
            rowGroupProps
          )}
          className={styles['sapphire-table__tfoot']}
          style={isVirtualized ? { height: '100%' } : {}}
          ref={ref}
        >
          <CollectionBranch
            collection={collection}
            parent={collection.footer}
          />
        </TFoot>
      </FooterContext.Provider>
    );
  }
);

export const InteractiveTableFooter = (forwardRef as forwardRefType)(
  function InteractiveTableFooter(
    props: ComponentProps<typeof TableFooter>,
    ref: ForwardedRef<HTMLTableSectionElement>
  ) {
    return (
      <FooterContext.Provider value={true}>
        <TableFooter {...props} ref={ref} />
      </FooterContext.Provider>
    );
  }
);

export interface InteractiveTableRowProps<T>
  extends Pick<
    RACRowProps<T>,
    | 'id'
    | 'textValue'
    | 'columns'
    | 'children' /* consider adding: isDisabled, onAction, dependencies, value*/
  > {
  /**
   * The expanded view to show when the row is expanded.
   * Adds another row to the table with the expanded view.
   * Does NOT work with virtualization.
   */
  expandedView?: ReactNode;
}

const virtualizedCellStyles = {
  alignContent: 'center',
  boxSizing: 'border-box',
  height: 'calc(100% - 1px)',
  width: '100%',
} as const;

export const InteractiveTableRow = (forwardRef as forwardRefType)(
  function InteractiveTableRow<T extends object>(
    {
      id,
      columns,
      children,
      expandedView,
      ...otherProps
    }: InteractiveTableRowProps<T>,
    ref: ForwardedRef<HTMLDivElement>
  ) {
    const {
      isDisabled,
      isInteractive,
      isNonVirtualized,
      selectionMode,
      disallowEmptySelection,
      highlightedRows,
    } = useContext(InteractiveTableOptionsContext)!;
    const { hasToggleExpansionColumn, expandedKeys, toggleRowExpansion } =
      useContext(ExpandedRowsStateContext)!;
    const isFooterRow = useContext(FooterContext);

    const expandedViewId = useId();
    const isExpanded = id != undefined && expandedKeys?.has(id);

    const onAction =
      expandedView && !isInteractive
        ? () => {
            if (id) {
              toggleRowExpansion?.(id);
            }
          }
        : undefined;

    const cellStyle = isNonVirtualized ? {} : virtualizedCellStyles;
    const rowStyle = isNonVirtualized ? {} : { height: '100%' };

    //  Patch for https://github.com/adobe/react-spectrum/issues/9937
    // Automatic id generation in react-aria was removed
    const patchedChildren =
      typeof children === 'function'
        ? (columnItem: T) => {
            const rendered = children(columnItem);
            if (
              React.isValidElement<{ id?: string | number }>(rendered) &&
              rendered.props.id != null &&
              !`${rendered.props.id}`.startsWith(`${id}:`)
            ) {
              return React.cloneElement(rendered, {
                id: `${id}:${rendered.props.id}`,
              });
            }
            return rendered;
          }
        : children;
    return (
      <>
        <RACRow
          // The ref type should have been more generic as it renders div or tr based on whether
          // virtualization is applied
          ref={ref as ForwardedRef<HTMLTableRowElement>}
          onAction={onAction}
          INTERNAL_nonActionable={isFooterRow}
          INTERNAL_nonSelectable={isFooterRow}
          isDisabled={isDisabled}
          id={id}
          {...otherProps}
          className={(renderProps) =>
            sapphireRowClasses({
              ...renderProps,
              isExpanded,
              isInteractive: Boolean(onAction),
              isHighlighted: highlightedRows?.includes(renderProps.id!),
            })
          }
          style={rowStyle}
        >
          {hasToggleExpansionColumn && (
            <RACCell
              className={getCellClassNames({
                align: 'center',
                overflow: 'wrap',
              })}
              style={{
                ...cellStyle,
                padding: 0, // maybe a modifier in the css module?
              }}
            >
              {expandedView && (
                <ToggleExpandButton
                  isExpanded={isExpanded}
                  expandedViewId={expandedViewId}
                  onPress={() => {
                    if (id != undefined) {
                      toggleRowExpansion?.(id);
                    }
                  }}
                />
              )}
            </RACCell>
          )}
          {selectionMode !== 'none' && (
            <RACCell isSticky className={checkboxCellClasses} style={cellStyle}>
              {!isFooterRow ? (
                selectionMode === 'single' && disallowEmptySelection ? (
                  <RadioBox slot="selection" />
                ) : (
                  <Checkbox INTERNAL_slot="selection" />
                )
              ) : null}
            </RACCell>
          )}
          <Collection items={columns}>{patchedChildren}</Collection>
        </RACRow>
        {isExpanded && (
          // Rendering RACRow because it's better to have the expanded view as a full width cell in
          // a normal grid row, so that the expanded view is included within the grid focus management
          // normally. The caveat is that we need to exclude this row from selection and row action.
          <RACRow
            INTERNAL_nonSelectable
            INTERNAL_nonActionable
            className={styles['sapphire-table__row-expanded-view']}
            style={rowStyle}
          >
            <InteractiveTableCell
              colSpan="remaining"
              INTERNAL_noPadding
              INTERNAL_shouldSelectOnPressUp
            >
              {expandedView}
            </InteractiveTableCell>
          </RACRow>
        )}
      </>
    );
  }
);

const stopPropagationProps: DOMAttributes<HTMLElement> = {
  onPointerDown: (e) => e.stopPropagation(),
  onMouseDown: (e) => e.stopPropagation(),
  onKeyDown: (e) => e.stopPropagation(),
};

export interface InteractiveTableCellProps extends Pick<RACCellProps, 'id'> {
  children?: ReactNode;
  /**
   * Use this prop to disable the table's keyboard navigation
   * while the focus is within this cell.
   *
   * This prop is meant to mitigate the issues when one needs
   * to render in the cell components which have keyboard
   * listeners that conflict with the table's.
   *
   * The long term solution will be to implement the "editable
   * grid pattern" recommended by ARIA
   * https://www.w3.org/WAI/ARIA/apg/patterns/grid/#gridNav_inside
   *
   * Issue in react-aria:
   * https://github.com/adobe/react-spectrum/issues/2328
   *
   * **NOTE**:
   * This should be used sparingly as the experience for end
   * users is not ideal.
   * @deprecated use <InteractiveTable.DisableKeyboardNav> to wrap interactive content in cells.
   **/
  disableKeyboardNav?: boolean; // TODO(v6): remove

  /**
   * A string representation of the cell contents, used for accessibility announcements.
   * Also used for the tooltip text that's shown when overflow is set to "truncate" and the cell
   * content doesn't fit. Useful when the cell content is not plain text. If not provided,
   * the text content of the cell is used for the tooltip.
   */
  textValue?: string;

  /**
   * Forcefully allow text selection on the cell, which is disabled by default on actionable rows.
   */
  forceAllowTextSelection?: boolean;

  /**
   * Indicates how many columns the data cell spans.
   *
   */
  colSpan?: number | 'remaining'; // TODO: document

  /**
   * A function that allows you to wrap the cell contents with custom
   * components.
   *
   * This is useful for those cases where you need something in the cell that
   * is not affected by the cell's overflow behaviour.
   */
  wrapCellContent?: (cellContent: React.ReactNode) => React.ReactNode;

  /**
   * Whether the default padding of the cell should not be applied.
   */
  INTERNAL_noPadding?: boolean;

  INTERNAL_shouldSelectOnPressUp?: boolean;
}

/**
 * A wrapper for interactive content that disable the table's keyboard navigation
 * while the focus is within its boundary.
 * It's meant to mitigate the issues when one needs
 * to render in the cell components which have keyboard
 * listeners that conflict with the table's.
 *
 * The long term solution will be to implement the "editable
 * grid pattern" recommended by ARIA
 * https://www.w3.org/WAI/ARIA/apg/patterns/grid/#gridNav_inside
 *
 * Issue in react-aria:
 * https://github.com/adobe/react-spectrum/issues/2328
 *
 * **NOTE**:
 * This should be used sparingly as the experience for end
 * users is not ideal.
 */
export function DisableKeyboardNav({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const state = useContext(TableStateContext)!;
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange: (isFocusWithin) => {
      state.setKeyboardNavigationDisabled(isFocusWithin);
    },
  });
  return (
    <div {...mergeProps(stopPropagationProps, focusWithinProps)}>
      {children}
    </div>
  );
}

// Reason for not just wrapping Cell from RAC: need to default size and align properties to the column
export const InteractiveTableCell = /*#__PURE__*/ createLeafComponent(
  'cell',
  (
    {
      INTERNAL_noPadding,
      INTERNAL_shouldSelectOnPressUp = false,
      wrapCellContent,
      ...props
    }: InteractiveTableCellProps,
    forwardedRef: ForwardedRef<HTMLTableCellElement>,
    cell: GridNode<unknown>
  ) => {
    const ref = useObjectRef<HTMLTableCellElement>(forwardedRef);
    const state = useContext(TableStateContext)!;

    const { focusWithinProps } = useFocusWithin({
      onFocusWithinChange: (isFocusWithin) => {
        if (props.disableKeyboardNav) {
          state.setKeyboardNavigationDisabled(isFocusWithin);
        }
      },
    });

    const { isVirtualized } = useContext(CollectionRendererContext);
    const tableOptions = useContext(InteractiveTableOptionsContext);

    const column = state.collection.columns[cell.colIndex ?? cell.index];
    cell.column = column;

    const { gridCellProps } = useTableCell(
      {
        node: cell,
        shouldSelectOnPressUp: INTERNAL_shouldSelectOnPressUp,
        isVirtualized,
      },
      state,
      ref
    );

    const cellOverflow =
      (column.props as InteractiveTableColumnProps).overflow ??
      tableOptions?.cellOverflow;

    const align = column.props.align;
    const { width, minWidth, maxWidth } =
      !props.colSpan && !isVirtualized
        ? column.props.style ?? {}
        : ({} as Record<string, undefined>);

    return (
      <SapphireTable.Cell
        {...mergeProps(
          filterDOMProps(props as object, { global: true }),
          gridCellProps,
          {
            tabIndex:
              gridCellProps.tabIndex === 0
                ? tableOptions?.tabIndex ?? gridCellProps.tabIndex
                : gridCellProps.tabIndex,
          },
          focusWithinProps,
          cell.props?.disableKeyboardNav && tableOptions?.isInteractive
            ? stopPropagationProps
            : {}
        )}
        elementType={(isVirtualized ? 'div' : 'td') as 'td'}
        textValue={cell.textValue}
        UNSAFE_style={{
          ...(isVirtualized
            ? { width: '100%', height: 'calc(100% - 1px)' }
            : {}),
          ...(INTERNAL_noPadding ? { padding: 0 } : {}),
        }}
        align={align}
        // FIXME: don't pass width props if table is resizable (using ResizableTableContainer)
        width={width}
        maxWidth={maxWidth}
        minWidth={minWidth}
        overflow={cellOverflow}
        wrapCellContent={wrapCellContent}
        ref={ref}
      >
        <CollectionRendererContext.Provider value={DefaultCollectionRenderer}>
          {cell.props.forceAllowTextSelection ? (
            <span
              style={{ cursor: 'text', userSelect: 'text' }}
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {props.children}
            </span>
          ) : (
            props.children
          )}
        </CollectionRendererContext.Provider>
      </SapphireTable.Cell>
    );
  }
) as (
  props: InteractiveTableCellProps & React.RefAttributes<HTMLElement>
) => ReactElement;

export interface InteractiveTableBodyProps<T>
  extends Pick<TableBodyProps<T>, 'items'> {
  children?: ReactNode;
}

export const InteractiveTableBody = (forwardRef as forwardRefType)(
  function TableBody<T extends object>(
    props: InteractiveTableBodyProps<T>,
    ref: ForwardedRef<HTMLTableSectionElement>
  ) {
    const { loadingState, loadingSkeletonRowsCount } = useContext(
      InteractiveTableOptionsContext
    )!;

    const { items, children } = props;

    const contentProps = !loadingState
      ? { items, children }
      : {
          children: (
            <>
              {loadingState === 'loadingMore' && (
                <Collection items={items}>{children}</Collection>
              )}
              {Array(loadingSkeletonRowsCount)
                .fill(null)
                .map((_, rowIndex) => {
                  return (
                    <SkeletonLoadingRow
                      key={'loading-' + rowIndex}
                      index={rowIndex}
                    />
                  );
                })}
            </>
          ),
        };

    return (
      <RACTableBody
        ref={ref}
        {...props}
        {...contentProps}
        dependencies={[loadingState, loadingSkeletonRowsCount]}
      />
    );
  }
);

function SkeletonLoadingRow({ index }: { index: number }) {
  const id = useId();
  const { isNonVirtualized } = useContext(InteractiveTableOptionsContext)!;
  const rowStyle = isNonVirtualized ? {} : { height: '100%' };

  return (
    <RACRow
      INTERNAL_nonSelectable
      INTERNAL_nonActionable
      style={rowStyle}
      className={sapphireRowClasses}
    >
      <MapColumns>
        {(column) => (
          <InteractiveTableCell id={`${id}:${column.index}`}>
            {column.props.renderSkeleton ? (
              column.props.renderSkeleton(index)
            ) : (
              <Skeleton.Text
                index={index}
                data-testid="skeletonBar"
                widthType={'randomPct'}
              />
            )}
          </InteractiveTableCell>
        )}
      </MapColumns>
    </RACRow>
  );
}
function MapColumns({
  children,
}: {
  children: (column: GridNode<object>) => React.ReactNode;
}) {
  const { columns } = useContext(ColumnsContext)!;
  // FIXME: when columns change, this renders wrong number of cells, causing the table to break.
  return (
    <>
      {useCachedChildren({
        items: columns,
        children,
      })}
    </>
  );
}
